import { Theme } from "@mui/material/styles";
import { ResponsiveStyleValue, SxProps } from "@mui/system";
import { CSSProperties } from "react";
import { GridSpacing } from "@mui/material";
import {
  getHighlightColorFromHex,
  AUTURA_ASPHALT_DARK,
  AUTURA_ASPHALT_LIGHT,
} from "context/themeContext";

// standard traxero-branded card header
export const cardHeaderSx: SxProps = {
  display: "flex",
  backgroundColor: "secondary.main",
  padding: 2,
  borderRadius: "0px",
  borderBottom: 2,
  borderColor: "primary.main",
};

// standard traxero-branded card border
export const cardSx: SxProps = {
  borderColor: "secondary.main",
  borderWidth: "2px",
  borderStyle: "solid",
  backgroundColor: "white",
};

export const cardSectionSx: SxProps = {
  borderColor: "secondary.main",
  borderTopLeftRadius: "0px",
  borderTopRightRadius: "30px",
};

export const cardSectionHeaderSx: SxProps = {
  backgroundColor: "secondary.main",
  borderRadius: "0px",
  paddingLeft: 4,
  paddingRight: 4,
};

export const toolbarSx: SxProps = {
  p: 2,
  backgroundColor: AUTURA_ASPHALT_LIGHT,
  borderBottom: "1px solid",
  borderColor: "secondary.main",
};

export const tabToolbarSx: SxProps = {
  backgroundColor: "secondary.main",
  borderBottom: "1px solid",
  borderColor: "primary.main",
};

export const toolbarSearchSx: SxProps = {
  backgroundColor: "white",
  borderRadius: 2,
};

// job builder stepper styles
export const stepperHeaderSx: SxProps = {
  backgroundColor: "secondary.main",
  py: 2.75,
  px: 2.75,
  borderBottomRadius: "0px",
  marginBottom: "16px",
  color: "secondary.contrastText",
};
export const stepLabelSx: SxProps = {
  "& .MuiStepLabel-label": {
    color: "secondary.contrastText",
    "&.Mui-completed": {
      opacity: 0.8,
      color: "secondary.contrastText",
    },
    "&.Mui-active": {
      color: "secondary.contrastText",
      fontWeight: "bold",
      opacity: 1,
    },
  },
  "& .MuiStepIcon-root": {
    color: "secondary.contrastText",
    opacity: 0.8,
    "&.Mui-completed": {
      opacity: 0.8,
    },
    "&.Mui-active": {
      opacity: 1,
    },
  },
};

// use this to put a label on top of a box with a border
export const overlineBoxLabel: SxProps = {
  position: "relative",
  top: -20,
  left: 15,
  backgroundColor: "white",
  px: 1,
  fontWeight: "bold",
};

export const redRadioSx: SxProps = {
  color: "error.main",
  "&.Mui-disabled": {
    color: "rgb(170,170,170) !important",
  },
  "&.Mui-checked": {
    color: "error.main",
  },
};

export const yellowRadioSx: SxProps = {
  color: "warning.main",
  "&.Mui-disabled": {
    color: "rgb(170,170,170) !important",
  },
  "&.Mui-checked": {
    color: "warning.main",
  },
};

export const greenRadioSx: SxProps = {
  color: "success.main",
  "&.Mui-disabled": {
    color: "rgb(170,170,170) !important",
  },
  "&.Mui-checked": {
    color: "success.main",
  },
};
export const summaryBoxSx: SxProps = {
  border: "1px solid",
  borderColor: AUTURA_ASPHALT_DARK,
  px: { xs: 2, lg: 4 },
  pb: 2,
};

export const activeListItemSx: SxProps = {
  width: "100%",
  bgcolor: "background.paper",
  "&:hover": {
    cursor: "pointer",
    backgroundColor: "#f5f5f5",
  },
};

export const readonlyListItemSx: SxProps = {
  width: "100%",
  bgcolor: "background.paper",
  "&:hover": {
    cursor: "default",
  },
};

export const formGridContainerSx: SxProps = {
  p: { xs: 2, sm: 4, lg: 8 },
};

export const formChildGridContainerSx: SxProps = {
  p: { xs: 2, lg: 4 },
};

export const formGridContainerSpacing: ResponsiveStyleValue<GridSpacing> = {
  xs: 0,
  sm: 2,
  lg: 4,
};

export const formChildGridContainerSpacing: ResponsiveStyleValue<GridSpacing> =
  {
    xs: 0,
    sm: 1,
    lg: 2,
  };

export const tabPanelSx: SxProps = {
  flex: 1,
  p: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

export const tabContainerSx: SxProps = {
  width: 160, // Fixed width for sidebar
  flexShrink: 0,
  borderRight: 1,
  borderColor: "divider",
  display: "flex",
  flexDirection: "column",
  bgcolor: "secondary.main",
};

export const vertTabContainerSx: SxProps = {
  backgroundColor: "secondary.main",
  width: "100%",
  height: 70,
  borderBottom: "2px solid",
  borderColor: "primary.main",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-around",
  flexDirection: "row",
};

export const tabContainerMobileSx: SxProps = {
  backgroundColor: AUTURA_ASPHALT_LIGHT,
  position: "fixed",
  width: "100%",
  zIndex: 1000,
  top: 100,
  right: 0,
  left: 0,
  borderRadius: 0,
  justifyContent: "center",
  display: "flex",
  height: 50,
};

export const tabPanelContainerSx: SxProps = {
  width: "100%",
  backgroundColor: "white",
};

export const tabSx: SxProps = {
  width: { sm: "auto", lg: "100%" },
  textAlign: { xs: "center", lg: "left" },
  borderRight: { xs: 0, sm: "1px solid", md: 0 },
  borderLeft: { xs: 0, sm: "1px solid", md: 0 },
  whiteSpace: "nowrap",
};

export const mobileCardSx: SxProps = {
  borderRadius: 0,
  borderBottom: "1px solid",
  borderColor: "secondary.main",
  backgroundColor: "white",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
};

export const mobileFixedToolbarSx: SxProps = {
  p: 2,
  backgroundColor: AUTURA_ASPHALT_LIGHT,
  borderBottom: "1px solid",
  borderColor: "secondary.main",
  position: "fixed",
  right: 0,
  left: 0,
  zIndex: 1000,
};

export const permissionsFormLabelSx: SxProps = {
  px: 4,
  flex: 1,
  my: { xs: 0, lg: "auto" },
  py: { xs: 1, lg: 0 },
};

export const permissionsHeaderSx: SxProps = {
  pt: 2,
  pb: { xs: 0, lg: 1 },
  px: { xs: 2, lg: 4 },
};

export const preStyle: CSSProperties = {
  lineHeight: "1.2",
  marginTop: "5px",
  marginBottom: "5px",
};
// For disabled fields
export const labelDisabledSx: SxProps = {
  ".MuiFormControlLabel-label.Mui-disabled": {
    color: "black !important",
  },
};

export const smallTableHeaderSx = {
  backgroundColor: AUTURA_ASPHALT_DARK,
  color: "white",
  fontWeight: "bold",
  cursor: "pointer",
};

export const smallTableRowSx = (i: number, theme: Theme): SxProps => ({
  background: i % 2 === 0 ? "white" : "whitesmoke",
  cursor: "pointer",
  "&:hover": {
    background: getHighlightColorFromHex(theme.palette.info.main),
  },
  // '& > *': { borderBottom: 'unset' },
});

export const dropzoneSx = (isDragActive: boolean): SxProps => ({
  borderWidth: "2px dashed",
  borderColor: isDragActive
    ? "info.main"
    : getHighlightColorFromHex("info.main"),
  borderRadius: 1,
  p: 2,
  textAlign: "center",
  background: `linear-gradient(90deg, primary.main 50%, transparent 0) repeat-x, linear-gradient(90deg, primary.main 50%, transparent 0) repeat-x, linear-gradient(0deg, primary.main 50%, transparent 0) repeat-y,linear-gradient(0deg, primary.main 50%, transparent 0) repeat-y`,
  backgroundSize: "12px 2px, 12px 2px, 2px 12px, 2px 12px",
  backgroundPosition: "0 0, 0 100%, 0 0, 100% 0",
  animation: isDragActive ? "rotatingBorder 0.6s linear infinite" : "",
});

export const accordionSummarySx: SxProps = {
  backgroundColor: "secondary.main",
  p: 0,
  border: 0,
  display: "flex",
  height: 62,
  borderBottom: "2px solid",
  borderColor: "primary.main",
  borderRadius: 0,
};
